import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import SeoMeta from '@/components/feature/SeoMeta';
import { videos, categories as allCategories } from '@/mocks/videos';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [visibleCount, setVisibleCount] = useState(8);

  const currentCategory = useMemo(
    () => allCategories.find((c) => c.slug === slug),
    [slug],
  );

  const filteredVideos = useMemo(
    () => videos.filter((v) => v.category === slug),
    [slug],
  );

  const displayedVideos = filteredVideos.slice(0, visibleCount);
  const hasMore = visibleCount < filteredVideos.length;

  const categoryName = currentCategory?.name || slug || '分类';

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setVisibleCount(8);
  }, [slug]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => prev + 8);
  }, []);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        name: `51黑料${categoryName} - 51黑料在线观看`,
        description: `51黑料${categoryName}分类，汇集最新最全的${categoryName}视频内容，高清流畅在线观看。`,
        url: `https://www.51heiliao.com/category/${slug}`,
        isPartOf: { '@type': 'WebSite', name: '51黑料' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.51heiliao.com/' },
          { '@type': 'ListItem', position: 2, name: categoryName, item: `https://www.51heiliao.com/category/${slug}` },
        ],
      },
    ],
  };

  const categoryMeta = {
    chigua: { title: '51黑料吃瓜视频 - 吃瓜视频在线观看 | 51黑料网', desc: '51黑料吃瓜视频专区，每日更新最新娱乐圈吃瓜视频、明星八卦、网络热点事件，高清流畅在线观看。', kw: '51黑料吃瓜视频,吃瓜视频,51黑料在线看,娱乐圈吃瓜,明星八卦视频' },
    heiliaovideo: { title: '51黑料视频 - 黑料视频在线观看 | 51黑料网', desc: '51黑料视频专区，汇集最新黑料实锤视频、网红翻车、圈内爆料等内容，51黑料在线观看高清不卡顿。', kw: '51黑料视频,黑料视频观看,51黑料网视频,黑料实锤,网红翻车视频' },
    yiqu: { title: '51黑料一区 - 一区在线观看 | 51黑料网', desc: '51黑料一区在线观看，提供一区完整视频资源，高清流畅播放，每日更新最新内容。', kw: '51黑料一区,一区在线,51黑料一区二区,51黑料在线视频' },
    erqu: { title: '51黑料二区 - 二区在线观看 | 51黑料网', desc: '51黑料二区在线观看，提供二区完整视频资源，高清流畅播放，每日更新最新内容。', kw: '51黑料二区,二区在线,51黑料一区二区在线,51黑料在线视频' },
    zhibo: { title: '51黑料直播 - 直播在线观看 | 51黑料网', desc: '51黑料直播专区，整合最新直播内容，涵盖娱乐直播、游戏直播等，高清流畅不卡顿。', kw: '51黑料直播,直播在线,51黑料在线观看,直播视频' },
    yingshi: { title: '51黑料影视 - 影视在线观看 | 51黑料网', desc: '51黑料影视专区，提供最新影视内容在线观看，高清画质流畅播放。', kw: '51黑料影视,影视在线,51黑料在线观看,影视视频' },
  };

  const meta = categoryMeta[slug as keyof typeof categoryMeta] || {
    title: `51黑料${categoryName} - 在线观看 | 51黑料网`,
    desc: `51黑料${categoryName}分类，汇集最新最全的${categoryName}视频内容，高清流畅在线观看。`,
    kw: `51黑料${categoryName},${categoryName}在线,51黑料在线观看`,
  };

  return (
    <>
      <SeoMeta
        title={meta.title}
        description={meta.desc}
        keywords={meta.kw}
        canonical={`https://www.51heiliao.com/category/${slug}`}
        schema={categorySchema}
      />

      <Navbar />

      {/* Category Hero */}
      <section className="relative w-full min-h-[280px] md:min-h-[340px] flex items-center mt-16">
        <img
          src="https://readdy.ai/api/search-image?query=Chinese%20entertainment%20video%20production%20studio%2C%20warm%20dramatic%20red%20and%20amber%20lighting%2C%20professional%20equipment%2C%20cinematic%20atmosphere%2C%20dark%20modern%20background%20with%20vibrant%20accents&width=1600&height=600&seq=cat-hero-generic&orientation=landscape"
          alt={'51黑料' + categoryName}
          className="absolute inset-0 w-full h-full object-cover object-top"
          title={'51黑料' + categoryName}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/55"></div>
        <div className="relative z-10 w-full px-4 md:px-6 max-w-7xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white font-heading">
            <strong>51黑料{categoryName}</strong>
          </h1>
          <p className="text-white/75 mt-3 max-w-lg mx-auto text-sm md:text-base">
            {meta.desc}
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <nav className="w-full max-w-7xl mx-auto px-4 md:px-6 py-3" aria-label="面包屑导航">
        <ol className="flex items-center text-xs text-foreground-500 gap-1.5">
          <li><Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link></li>
          <li><i className="ri-arrow-right-s-line"></i></li>
          <li className="text-foreground-700 font-medium">{categoryName}</li>
        </ol>
      </nav>

      {/* Content */}
      <main className="w-full max-w-7xl mx-auto px-4 md:px-6 pb-16">
        {/* Category Tabs */}
        <div className="flex items-center flex-wrap gap-2 mb-8">
          {allCategories.slice(1).map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.slug}`}
              className={`px-5 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                slug === cat.slug
                  ? 'bg-primary-500 text-background-50 shadow-sm'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {cat.name}
            </Link>
          ))}
        </div>

        {/* Video Grid */}
        {filteredVideos.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-video-line text-2xl"></i>
            </div>
            <p className="text-foreground-600">该分类暂无视频内容</p>
            <Link
              to="/"
              className="inline-block mt-3 text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer"
            >
              返回首页浏览更多视频
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {displayedVideos.map((video) => (
                <VideoCard
                  key={video.id}
                  id={video.id}
                  title={video.title}
                  slug={video.slug}
                  thumbnail={video.thumbnail}
                  category={video.category}
                  categoryName={categoryName}
                  views={video.views}
                  date={video.date}
                  duration={video.duration}
                  isHot={video.isHot}
                />
              ))}
            </div>

            {hasMore && (
              <div className="mt-10 text-center">
                <button
                  onClick={handleLoadMore}
                  className="px-10 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 hover:text-foreground-900 font-medium text-sm rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  加载更多视频 <i className="ri-arrow-down-line ml-1"></i>
                </button>
              </div>
            )}
          </>
        )}

        {/* SEO Content */}
        {filteredVideos.length > 0 && (
          <section className="mt-16 p-6 bg-background-50 rounded-lg border border-background-200/70">
            <h2 className="text-lg font-semibold text-foreground-900 font-heading mb-3">
              关于51黑料{categoryName}
            </h2>
            <p className="text-sm text-foreground-700 leading-relaxed">
              51黑料{categoryName}频道为您提供最新、最全的{categoryName}内容。
              51黑料在线观看平台每日更新海量视频资源，确保您不错过任何精彩内容。
              所有51黑料视频均支持高清流畅播放，为您带来最佳的在线观看体验。
              无论您是在寻找51黑料在线视频还是51黑料网视频，这里都是您的最佳选择。
            </p>
          </section>
        )}
      </main>

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 shadow-lg hover:bg-primary-600 transition-all duration-200 z-40 cursor-pointer"
          aria-label="返回顶部"
        >
          <i className="ri-arrow-up-line"></i>
        </button>
      )}

      <Footer />
    </>
  );
}